import mitt from "mitt";
import type { FC, PropsWithChildren } from "react";
import { createContext, lazy, Suspense, useCallback, useState } from "react";
import { useLocation } from "react-router";

import type { SiteSdk } from "~/core/site-sdk";

const Modals = lazy(() =>
  import("~/widgets/auth-modals/modals").then((module) => ({
    default: module.Modals,
  })),
);

export type AuthModalsContext = {
  open: (name: AuthModalName) => void;
  close: () => void;
};

// eslint-disable-next-line react-refresh/only-export-components
export const authModalsProvider = createContext<AuthModalsContext | null>(null);

export type Events = {
  open: AuthModalName;
  close: null;
};

export type AuthModalName =
  | "login"
  | "register"
  | "restore"
  | "successRestore"
  | "successRegistration"
  | "fastRegistration"
  | "valueChainRegistration"
  | "valueChainLogin"
  | "otpRegistration"
  | "preDepBonus"
  | "changeDisplayNameWidget"
  | "appDownloadModal";

const emitter = mitt<Events>();

// todo нужно переименовать на ModalsProvider
export type AuthModalsProviderProps = PropsWithChildren<{
  siteSdk: SiteSdk;
  playerId?: string | undefined;
}>;

export const AuthModalsProvider: FC<AuthModalsProviderProps> = ({
  children,
  ...props
}) => {
  const { search } = useLocation();
  const [pendingOpen, setPendingOpen] = useState<AuthModalName | null>(null);
  const [modalsRequested, setModalsRequested] = useState(false);
  const shouldRenderModals =
    modalsRequested || new URLSearchParams(search).has("openModal");

  const close = (): void => {
    emitter.emit("close", null);
  };

  const open = (name: AuthModalName): void => {
    setModalsRequested(true);
    setPendingOpen(name);
  };

  const handlePendingOpenHandled = useCallback((): void => {
    setPendingOpen(null);
  }, []);

  return (
    <authModalsProvider.Provider
      value={{
        open,
        close,
      }}
    >
      <>
        {children}
        {shouldRenderModals && (
          <Suspense fallback={null}>
            <Modals
              emitter={emitter}
              pendingOpen={pendingOpen}
              onPendingOpenHandled={handlePendingOpenHandled}
              {...props}
            />
          </Suspense>
        )}
      </>
    </authModalsProvider.Provider>
  );
};
